<template>
  <div class="projects-list">
    <Loader :loading="showLoader" />

    <v-toolbar color="#222222" flat dense class="mb-2" style="cursor: pointer">
      <v-icon color="#337b52" class="ml-2" size="40" @click="router.go(-1)">mdi-arrow-left-bold-circle</v-icon>
      <v-toolbar-title style="font-family: 'Material Design Icons'; font-size: 25px !important">
        Art-Invest Projekte
      </v-toolbar-title>
    </v-toolbar>

    <!-- Suche & Filter bleiben unverändert -->
    <v-container class="py-3">
      <v-row justify="center" class="search-wrapper">
        <v-col cols="12" md="8">
          <v-text-field v-model="search" prepend-inner-icon="mdi-magnify" label="Projekt nach Namen oder Ort suchen"
            single-line hide-details variant="solo-inverted" density="compact" class="elegant-search" full-width />
        </v-col>
        <v-col cols="12" md="4" class="d-flex justify-center justify-md-start mt-3 mt-md-0">
          <v-menu :location="'start'" max-height="500px">
            <template #activator="{ props }">
              <v-btn color="#337b52" dark v-bind="props" class="filter-button">
                <v-icon left size="small">mdi-filter</v-icon>
                Suche nach Stadt
              </v-btn>
            </template>
            <v-list>
              <v-list-item @click="filterByLocation('All')">
                <v-list-item-title>Alle</v-list-item-title>
              </v-list-item>
              <v-list-item v-for="loc in uniqueLocations" :key="loc" @click="filterByLocation(loc)">
                <v-list-item-title>{{ loc }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-col>
      </v-row>
    </v-container>

    <!-- Hier verwenden wir jetzt CSS-Grid -->
    <v-container>
      <div class="project-grid">
        <div v-for="project in filteredProjects" :key="project.id" class="project-card"
          @click="openProjectLink(project.guid.rendered)">
          <v-img height="200px" :src="project.featured_image" cover class="project-img"></v-img>
          <div class="card-body">
            <h3 class="title">{{ project.title.rendered }}</h3>
            <p class="subtitle">{{ project.place }}</p>
            <p class="description">{{ project.subtitle }}</p>
          </div>
        </div>
      </div>
    </v-container>
  </div>
</template>

<script>
import { onMounted, ref, computed } from "vue";
import Loader from "@/components/Loader.vue";
import { useRouter } from "vue-router";
import { getArtinvestProjects } from "@/services/ArtinvestFixedData/ArtinvestProjects.js";

export default {
  name: "ProjectsList",
  components: { Loader },
  setup() {
    const router = useRouter();
    const showLoader = ref(true);
    const projectsList = ref([]);
    const search = ref("");
    const locations = ref([]);
    const currentFilter = ref("All");

    onMounted(() => {
      const projects = getArtinvestProjects();
      locations.value = [
        ...new Set(
          projects
            .map((p) => p.place.trim())
            .filter((place) => place)
        ),
      ];
      projectsList.value = projects;
      showLoader.value = false;
    });

    const uniqueLocations = computed(() => locations.value);

    const filteredProjects = computed(() => {
      return projectsList.value.filter((p) => {
        const text = search.value.toLowerCase();
        const matchSearch =
          p.title.rendered.toLowerCase().includes(text) ||
          p.place.toLowerCase().includes(text);
        const matchFilter =
          currentFilter.value === "All" ||
          p.place === currentFilter.value;
        return matchSearch && matchFilter;
      });
    });

    const filterByLocation = (loc) => {
      currentFilter.value = loc;
      search.value = loc === "All" ? "" : loc;
    };

    const openProjectLink = (url) => {
      window.open(url, "_blank");
    };

    return {
      router,
      showLoader,
      search,
      uniqueLocations,
      filteredProjects,
      filterByLocation,
      openProjectLink,
    };
  },
};
</script>

<style scoped>
/* Search & Filter Styling */
.search-wrapper {
  max-width: 900px;
  margin: 0 auto 6px;
  /* centers row and adds bottom spacing */
}

.elegant-search {
  max-width: 600px;
  margin: 0 auto;
  /* centers the search field */
}

.filter-button {
  min-width: 120px;
}

/* Vuetify solo-inverted input adjustments */
.elegant-search .v-text-field__slot {
  padding: 10px;
  border-radius: 5px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.2);
}

.elegant-search input {
  font-size: 16px;
}

/* Project Grid: always 4 columns on large */
.project-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  padding: 0 16px;
}

/* Individual Card Styling */
.project-card {
  cursor: pointer;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  background: #fff;
  transition: transform 0.2s, box-shadow 0.2s;
}

.project-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.15);
}

.project-img {
  width: 100%;
}

.card-body {
  padding: 12px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.title {
  margin: 0;
  font-size: 1rem;
  font-weight: bold;
  line-height: 1.2;
}

.subtitle {
  margin: 4px 0;
  font-size: 0.9rem;
  color: #337b52;
}

.description {
  margin-top: auto;
  font-size: 0.9rem;
}

/* Responsive adjustments */
@media (max-width: 1200px) {
  .project-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 800px) {
  .project-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 500px) {
  .project-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .filter-button {
    width: 100%;
    margin-top: 10px;
  }
}
</style>
